import store from "../redux/store";
import API from "../urls/api";
import { authToken } from "../utils/storage/storage";
import { DeviceEnum } from "../redux/slices/environmentSlice";
import { ENVIRONMENT } from "../environment";
import { toast } from "mobin-toast";
import { toastMessages } from "./toastMessages";
import { setUser } from "../redux/slices/authSlice";

const requestDelay = 1000;

export const isUnauthorized = (status: number) => {
    return status === 401;
};

export function generateHeader(object: any = {}, lang = "fa", auth = true) {
    const device = store.getState().environment.device;
    const header: { [k: string]: any } = {};
    header["user-device"] = device === DeviceEnum.desktop ? "web-desktop" : "web-mobile";
    header["ACCEPT-LANGUAGE"] = lang;
    if (authToken.get() && auth) {
        header["Authorization"] = "Bearer " + authToken.get()?.access;
    }
    for (const key of Object.keys(object)) {
        header[key] = object[key];
    }
    return header;
}

export function generateNewAccessToken(refresh?: string) {
    return new Promise((resolve) => {
        if (authToken.get()?.refresh || refresh) {
            post(
                (data, status) => {
                    if (responseValidator(status)) {
                        const token = {
                            access: data.access,
                            refresh: refresh ? refresh : authToken.get()?.refresh,
                            token_id: authToken.get()?.token_id ? authToken.get()?.token_id : data.token_id
                        };
                        authToken.set(token);
                        resolve(token);
                    } else {
                        resolve(false);
                        authToken.remove();
                        store.dispatch(setUser(null));
                        window.location.replace(`${ENVIRONMENT.landingBaseUrl}auth/login?redirect=${btoa(window.location.href)}`);
                    }
                },
                API.auth.refresh(),
                { refresh: refresh ? refresh : authToken.get()?.refresh }
            );
        }
    });
}

export function get(
    event: (response: any, status: number) => void,
    url: string,
    params: { [k: string]: any } = {},
    lang = "fa",
    auth = true,
    signal?: any
) {
    const generatedUrl = new URL(url);
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined) {
            generatedUrl.searchParams.append(key, params[key]);
        }
    });
    let status: number;
    return fetch(generatedUrl.href, {
        method: "GET",
        headers: generateHeader({ "Content-Type": "application/json" }, lang, auth),
        signal
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    get(event, url, params, lang, auth);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    get(event, url, params, lang, auth);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function getAll(event: (response: any, status: number) => void, url: string, params: any = {}, lang = "fa", signal?: any) {
    let status: number;
    const generatedUrl = new URL(url);
    const limit = params.limit ? params.limit : 1000;
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined) {
            generatedUrl.searchParams.append(key, params[key]);
        }
    });
    generatedUrl.searchParams.append("limit", limit.toString());
    fetch(generatedUrl.href, {
        method: "GET",
        headers: generateHeader({ "Content-Type": "application/json" }, lang, true),
        signal
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    getAll(event, url, params, lang);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    getAll(event, url, params, lang);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            new Promise(async (resolve) => {
                if (data.results) {
                    if (data.count > limit) {
                        let result: any = [...data.results];
                        const count = data.count;
                        const requestCount = Math.ceil(count / limit);
                        if (requestCount > 0) {
                            let counter = 1;
                            for (let i = 1; i <= requestCount; i++) {
                                await get(
                                    (response) => {
                                        if (response.results) {
                                            counter++;
                                            result = [...result, ...response.results];
                                        }
                                        if (counter > requestCount) {
                                            resolve({ response: null, result });
                                            return;
                                        }
                                    },
                                    url,
                                    { ...params, offset: limit * i, limit }
                                );
                            }
                        } else {
                            resolve({ response: null, result });
                        }
                    } else {
                        resolve({ response: null, result: [...data.results] });
                    }
                }
            }).then((data) => {
                event(data, status);
            });
        })
        .catch((err) => {
            event(err, status);
        });
}

export function post(event: (data: any, status: number) => void, url: string, body: any, lang = "fa", signal?: AbortSignal) {
    let status: number;
    fetch(url, {
        method: "POST",
        headers: generateHeader({ "Content-Type": "application/json" }, lang),
        body: JSON.stringify(body),
        signal
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    post(event, url, body, lang);
                });
            }
            // if (status === 429) {
            //     setTimeout(() => {
            //         post(event, url, body, lang);
            //     }, requestDelay);
            // }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function form(event: (data: any, status: number) => void, url: string, body: any, lang = "fa") {
    let status: number;
    fetch(url, {
        method: "POST",
        body: body,
        headers: generateHeader({}, lang)
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    form(event, url, body, lang);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    form(event, url, body, lang);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function put(event: (data: any, status: number) => void, url: string, body: any, json = true, lang = "fa") {
    let status: number;
    fetch(url, {
        method: "PUT",
        headers: generateHeader({ "Content-Type": "application/json" }, lang),
        body: JSON.stringify(body)
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    put(event, url, body, json, lang);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    put(event, url, body, json, lang);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function patch(event: (data: any, status: number) => void, url: string, body: any, json = true, lang = "fa") {
    let status: number;
    fetch(url, {
        method: "PATCH",
        headers: generateHeader({ "Content-Type": "application/json" }, lang),
        body: JSON.stringify(body)
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    patch(event, url, body, json, lang);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    patch(event, url, body, json, lang);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function del(event: (data: any, status: number) => void, url: string, body?: any, lang = "fa") {
    let status: number;
    fetch(url, {
        method: "DELETE",
        body: JSON.stringify(body),
        headers: generateHeader({ "Content-Type": "application/json" }, lang)
    })
        .then(function (response) {
            status = response.status;
            if (authToken.get() && status === 401) {
                generateNewAccessToken().then(() => {
                    del(event, url, body, lang);
                });
            }
            if (status === 429) {
                setTimeout(() => {
                    del(event, url, body, lang);
                }, requestDelay);
            }
            return response.json();
        })
        .then(function (data) {
            event(data, status);
        })
        .catch((err) => {
            event(err, status);
        });
}

export function responseValidator(status: number): boolean {
    if (!status) {
        return false;
    } else {
        return status >= 200 && status < 300;
    }
}

export function advancedResponseValidator(data: any, status: number) {
    if (status >= 200 && status < 300) {
        return true;
    } else {
        if (status >= 500) {
            toast({ title: toastMessages(14), type: "error" });
            return false;
        }
        if (status !== 401) {
            if (data.detail) {
                if (Array.isArray(data.detail)) {
                    data.detail.forEach((err: string) => toast({ title: !!toastMessages(15) ? toastMessages(15) : err, type: "error" }));
                } else {
                    toast({ title: !!toastMessages(16) ? toastMessages(16) : data.detail, type: "error" });
                }
            }
        }
        return false;
    }
}
